import React, { useEffect, useRef, memo, useState, useCallback } from "react";
import { useUpdateNodeInternals, useReactFlow, useViewport } from "@xyflow/react";
import { drag } from "d3-drag";
import { select } from "d3-selection";

const getHandleStyle = (scale = 1) => ({
  width: `${14 * scale}px`,
  height: `${14 * scale}px`,
  backgroundColor: "white",
  border: `${2 * scale}px solid #06b6d4`,
  borderRadius: `${5 * scale}px`,
  cursor: "pointer",
  boxShadow: `0 ${2 * scale}px ${4 * scale}px rgba(0,0,0,0.1)`,
});

const ROTATION_SNAP_STEP = 15;
const ROTATION_SNAP_THRESHOLD = 3;

const normalizeRotation = (rotation) => ((rotation % 360) + 360) % 360;

const snapRotation = (rotation) => {
  const normalizedRotation = normalizeRotation(rotation);
  const snappedRotation =
    Math.round(normalizedRotation / ROTATION_SNAP_STEP) * ROTATION_SNAP_STEP;
  const normalizedSnappedRotation = normalizeRotation(snappedRotation);
  const distance = Math.abs(normalizedRotation - normalizedSnappedRotation);
  const wrappedDistance = Math.min(distance, 360 - distance);

  if (wrappedDistance <= ROTATION_SNAP_THRESHOLD) {
    return normalizedSnappedRotation;
  }

  return Math.round(normalizedRotation);
};

const resizeHandleConfigs = [
  {
    position: "top-left",
    cursor: "nwse-resize",
    style: { left: 0, top: 0, transform: "translate(-50%, -50%)" },
  },
  {
    position: "top",
    cursor: "ns-resize",
    style: { left: "50%", top: 0, transform: "translate(-50%, -50%)" },
  },
  {
    position: "top-right",
    cursor: "nesw-resize",
    style: { left: "100%", top: 0, transform: "translate(-50%, -50%)" },
  },
  {
    position: "right",
    cursor: "ew-resize",
    style: { left: "100%", top: "50%", transform: "translate(-50%, -50%)" },
  },
  {
    position: "bottom-right",
    cursor: "nwse-resize",
    style: { left: "100%", top: "100%", transform: "translate(-50%, -50%)" },
  },
  {
    position: "bottom",
    cursor: "ns-resize",
    style: { left: "50%", top: "100%", transform: "translate(-50%, -50%)" },
  },
  {
    position: "bottom-left",
    cursor: "nesw-resize",
    style: { left: 0, top: "100%", transform: "translate(-50%, -50%)" },
  },
  {
    position: "left",
    cursor: "ew-resize",
    style: { left: 0, top: "50%", transform: "translate(-50%, -50%)" },
  },
];

export const SelectionOverlay = memo(
  ({
    selected,
    onRotate,
    onResize,
    minWidth = 50,
    minHeight = 30,
    nodeId,
    width = 100,
    height = 100,
    rotation = 0,
    nodeX = 0,
    nodeY = 0,
  }) => {
    const rotateControlRef = useRef(null);
    const updateNodeInternals = useUpdateNodeInternals();
    const { getNode } = useReactFlow();
    const { zoom } = useViewport();
    const viewportScale = 1 / Math.max(zoom || 1, 0.01);
    // State for tracking resize and showing dimensions
    const [isResizing, setIsResizing] = useState(false);
    const [currentDimensions, setCurrentDimensions] = useState({
      width,
      height,
    });
    const [activeHandle, setActiveHandle] = useState(null);
    const hideTimeoutRef = useRef(null);
    const resizeStartRef = useRef(null);
    // State for tracking rotation - for immediate visual feedback
    const [isRotating, setIsRotating] = useState(false);
    const [currentRotation, setCurrentRotation] = useState(rotation);

    // Sync rotation from props when not actively rotating
    useEffect(() => {
      if (!isRotating) {
        setCurrentRotation(rotation);
      }
    }, [rotation, isRotating]);

    // Update current dimensions when props change
    useEffect(() => {
      if (!isResizing) {
        setCurrentDimensions({ width, height });
      }
    }, [width, height, isResizing]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }
      };
    }, []);

    // Custom resize handler that accounts for rotation
    const handleResizeStart = useCallback(
      (e, position) => {
        e.stopPropagation();
        e.preventDefault();
        const node = getNode(nodeId);
        if (!node) return;
        setIsResizing(true);
        setActiveHandle(position);
        resizeStartRef.current = {
          startX: e.clientX,
          startY: e.clientY,
          startWidth: width,
          startHeight: height,
          startNodeX: node.position.x,
          startNodeY: node.position.y,
        };
        document.body.style.cursor = getCursorForPosition(position, rotation);
        document.body.style.userSelect = "none";
        // Clear any existing timeout
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }
      },
      [nodeId, getNode, width, height, rotation],
    );

    const handleResizeMove = useCallback(
      (e) => {
        if (!resizeStartRef.current || !activeHandle) return;
        const {
          startX,
          startY,
          startWidth,
          startHeight,
          startNodeX,
          startNodeY,
        } = resizeStartRef.current;
        // Calculate mouse movement in screen space
        const deltaX = (e.clientX - startX) * viewportScale;
        const deltaY = (e.clientY - startY) * viewportScale;
        // Convert rotation to radians
        const rad = (rotation * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        // Transform mouse delta to local (rotated) coordinate system
        const localDeltaX = deltaX * cos + deltaY * sin;
        const localDeltaY = -deltaX * sin + deltaY * cos;
        let newWidth = startWidth;
        let newHeight = startHeight;
        let newX = startNodeX;
        let newY = startNodeY;
        // Calculate new dimensions based on handle position
        switch (activeHandle) {
          case "right":
            newWidth = Math.max(minWidth, startWidth + localDeltaX);
            break;
          case "left": {
            newWidth = Math.max(minWidth, startWidth - localDeltaX);
            // Adjust position to keep right edge fixed
            const widthDiffLeft = newWidth - startWidth;
            newX = startNodeX - (widthDiffLeft * cos) / 2;
            newY = startNodeY - (widthDiffLeft * sin) / 2;
            break;
          }
          case "bottom":
            newHeight = Math.max(minHeight, startHeight + localDeltaY);
            break;
          case "top": {
            newHeight = Math.max(minHeight, startHeight - localDeltaY);
            // Adjust position to keep bottom edge fixed
            const heightDiffTop = newHeight - startHeight;
            newX = startNodeX + (heightDiffTop * sin) / 2;
            newY = startNodeY - (heightDiffTop * cos) / 2;
            break;
          }
          case "top-right":
            newWidth = Math.max(minWidth, startWidth + localDeltaX);
            newHeight = Math.max(minHeight, startHeight - localDeltaY);
            {
              const heightDiff = newHeight - startHeight;
              newX = startNodeX + (heightDiff * sin) / 2;
              newY = startNodeY - (heightDiff * cos) / 2;
            }
            break;
          case "top-left":
            newWidth = Math.max(minWidth, startWidth - localDeltaX);
            newHeight = Math.max(minHeight, startHeight - localDeltaY);
            {
              const widthDiff = newWidth - startWidth;
              const heightDiff = newHeight - startHeight;
              newX =
                startNodeX - (widthDiff * cos) / 2 + (heightDiff * sin) / 2;
              newY =
                startNodeY - (widthDiff * sin) / 2 - (heightDiff * cos) / 2;
            }
            break;
          case "bottom-right":
            newWidth = Math.max(minWidth, startWidth + localDeltaX);
            newHeight = Math.max(minHeight, startHeight + localDeltaY);
            break;
          case "bottom-left":
            newWidth = Math.max(minWidth, startWidth - localDeltaX);
            newHeight = Math.max(minHeight, startHeight + localDeltaY);
            {
              const widthDiff = newWidth - startWidth;
              newX = startNodeX - (widthDiff * cos) / 2;
              newY = startNodeY - (widthDiff * sin) / 2;
            }
            break;
        }
        // Update dimensions for display
        setCurrentDimensions({
          width: Math.round(newWidth),
          height: Math.round(newHeight),
        });
        // Call the parent resize handler
        if (onResize) {
          onResize(Math.round(newWidth), Math.round(newHeight), {
            x: Math.round(newX),
            y: Math.round(newY),
          });
        }
      },
      [activeHandle, rotation, minWidth, minHeight, onResize, viewportScale],
    );

    const handleResizeEnd = useCallback(() => {
      setActiveHandle(null);
      resizeStartRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      hideTimeoutRef.current = setTimeout(() => {
        setIsResizing(false);
      }, 1000);
    }, []);

    // Add/remove global event listeners for resize
    useEffect(() => {
      if (activeHandle) {
        window.addEventListener("pointermove", handleResizeMove);
        window.addEventListener("pointerup", handleResizeEnd);
        return () => {
          window.removeEventListener("pointermove", handleResizeMove);
          window.removeEventListener("pointerup", handleResizeEnd);
        };
      }
    }, [activeHandle, handleResizeMove, handleResizeEnd]);

    // Helper function to get cursor based on position and rotation
    const getCursorForPosition = (position, rot) => {
      // Normalize rotation to 0-360
      const normalizedRot = ((rot % 360) + 360) % 360;
      // Base cursors for each position (at 0 rotation)
      const baseCursors = {
        top: 0,
        "top-right": 45,
        right: 90,
        "bottom-right": 135,
        bottom: 180,
        "bottom-left": 225,
        left: 270,
        "top-left": 315,
      };
      // Calculate effective angle
      const effectiveAngle = (baseCursors[position] + normalizedRot) % 360;
      // Map angle to cursor
      if (effectiveAngle >= 337.5 || effectiveAngle < 22.5) return "ns-resize";
      if (effectiveAngle >= 22.5 && effectiveAngle < 67.5) return "nesw-resize";
      if (effectiveAngle >= 67.5 && effectiveAngle < 112.5) return "ew-resize";
      if (effectiveAngle >= 112.5 && effectiveAngle < 157.5)
        return "nwse-resize";
      if (effectiveAngle >= 157.5 && effectiveAngle < 202.5) return "ns-resize";
      if (effectiveAngle >= 202.5 && effectiveAngle < 247.5)
        return "nesw-resize";
      if (effectiveAngle >= 247.5 && effectiveAngle < 292.5) return "ew-resize";
      return "nwse-resize";
    };

    // Setup d3-drag for rotation - following React Flow example pattern
    useEffect(() => {
      if (!rotateControlRef.current || !selected) {
        return;
      }

      const selection = select(rotateControlRef.current);
      // Get the center of the node for rotation calculations
      const getNodeCenter = () => {
        const node = document.querySelector(
          `.react-flow__node[data-id="${nodeId}"]`,
        );
        if (node) {
          const rect = node.getBoundingClientRect();
          return {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
          };
        }
        return { x: 0, y: 0 };
      };

      const dragHandler = drag()
        .on("start", () => {
          // Set cursor during drag
          document.body.style.cursor = "grabbing";
          document.body.style.userSelect = "none";
          setIsRotating(true);
        })
        .on("drag", (evt) => {
          // Get the center of the node
          const center = getNodeCenter();
          // Calculate angle from center to current mouse position
          // evt.sourceEvent contains the actual mouse event
          const mouseX = evt.sourceEvent.clientX;
          const mouseY = evt.sourceEvent.clientY;
          const dx = mouseX - center.x;
          const dy = mouseY - center.y;
          // Calculate rotation angle (atan2 gives angle from positive x-axis)
          // We adjust by -90 degrees because the handle is at the top
          const rad = Math.atan2(dy, dx);
          const deg = rad * (180 / Math.PI);
          // Adjust so 0 degrees is when handle is at top
          let newRotation = deg + 90;
          newRotation = snapRotation(newRotation);
          // Update local state IMMEDIATELY for visual feedback
          setCurrentRotation(newRotation);
          // Also update parent (this will eventually sync back, but local state is immediate)
          if (onRotate) {
            onRotate(newRotation);
          }
          // Update node internals so React Flow knows about the rotation
          updateNodeInternals(nodeId);
        })
        .on("end", () => {
          // Reset cursor after drag
          document.body.style.cursor = "";
          document.body.style.userSelect = "";
          setIsRotating(false);
        });

      selection.call(dragHandler);

      // Cleanup
      return () => {
        selection.on(".drag", null);
      };
    }, [nodeId, selected, onRotate, updateNodeInternals]);

    if (!selected) return null;

    // Use local rotation state for immediate visual feedback
    const displayRotation = currentRotation;

    return (
      <>
        {/* Container that rotates with the object - includes dashed border and resize handles */}
        <div
          className="absolute selection-overlay-container"
          style={{
            transform: `rotate(${displayRotation}deg)`,
            transformOrigin: "center center",
            zIndex: 9999,
            pointerEvents: "none",
            inset: `${-2 * viewportScale}px`,
          }}
        >
          {/* Dashed border outline - rotates with object */}
          <div
            className="absolute"
            style={{
              inset: `${2 * viewportScale}px`,
              border: `${2 * viewportScale}px dashed #06b6d4`,
              pointerEvents: "none",
            }}
          />

          {/* Dimension display - shows W × H at bottom center during resize */}
          {isResizing && (
            <div
              style={{
                position: "absolute",
                left: "50%",
                bottom: `${-28 * viewportScale}px`,
                transform: "translateX(-50%)",
                backgroundColor: "#0ea5e9",
                color: "white",
                padding: `${2 * viewportScale}px ${8 * viewportScale}px`,
                borderRadius: `${4 * viewportScale}px`,
                fontSize: `${11 * viewportScale}px`,
                fontWeight: 500,
                fontFamily: "Inter, system-ui, sans-serif",
                whiteSpace: "nowrap",
                zIndex: 1002,
                boxShadow: `0 ${1 * viewportScale}px ${3 * viewportScale}px rgba(0,0,0,0.2)`,
                pointerEvents: "none",
              }}
            >
              {currentDimensions.width} × {currentDimensions.height}
            </div>
          )}

          {/* Rotation angle display - shows angle during rotation */}
          {isRotating && (
            <div
              style={{
                position: "absolute",
                left: "50%",
                top: `${-62 * viewportScale}px`,
                transform: "translateX(-50%)",
                backgroundColor: "#0ea5e9",
                color: "white",
                padding: `${2 * viewportScale}px ${8 * viewportScale}px`,
                borderRadius: `${4 * viewportScale}px`,
                fontSize: `${11 * viewportScale}px`,
                fontWeight: 500,
                fontFamily: "Inter, system-ui, sans-serif",
                whiteSpace: "nowrap",
                zIndex: 1002,
                boxShadow: `0 ${1 * viewportScale}px ${3 * viewportScale}px rgba(0,0,0,0.2)`,
                pointerEvents: "none",
              }}
            >
              {Math.round(displayRotation)}°
            </div>
          )}

          {/* Rotation handle and connecting line - positioned relative to rotated content */}
          <div
            ref={rotateControlRef}
            className="nodrag rotation-handle"
            style={{
              position: "absolute",
              width: `${14 * viewportScale}px`,
              height: `${14 * viewportScale}px`,
              backgroundColor: "white",
              border: `${2 * viewportScale}px solid #06b6d4`,
              borderRadius: `${5 * viewportScale}px`,
              left: "50%",
              top: `${-58 * viewportScale}px`,
              transform: "translateX(-50%)",
              cursor: isRotating ? "grabbing" : "grab",
              zIndex: 1001,
              pointerEvents: "auto",
              boxShadow: `0 ${2 * viewportScale}px ${4 * viewportScale}px rgba(0,0,0,0.1)`,
            }}
          />

          {/* Connecting line from node to rotation handle */}
          <div
            style={{
              position: "absolute",
              width: `${2 * viewportScale}px`,
              height: `${54 * viewportScale}px`,
              backgroundColor: "transparent",
              borderLeft: `${2 * viewportScale}px dashed #06b6d4`,
              left: "50%",
              top: `${-54 * viewportScale}px`,
              transform: "translateX(-50%)",
              zIndex: 1000,
              pointerEvents: "none",
            }}
          />

          {/* Resize handles - all 8 positions, functional and visual */}
          {resizeHandleConfigs.map(({ position, cursor, style }) => {
            const dynamicCursor = getCursorForPosition(
              position,
              displayRotation,
            );
            return (
              <div
                key={position}
                className="nodrag resize-handle"
                data-cursor={dynamicCursor}
                onPointerDown={(e) => handleResizeStart(e, position)}
                style={{
                  ...getHandleStyle(viewportScale),
                  ...style,
                  position: "absolute",
                  zIndex: 1001,
                  pointerEvents: "auto",
                }}
              />
            );
          })}
        </div>
      </>
    );
  },
);

SelectionOverlay.displayName = "SelectionOverlay";
